import { Check, Loader2, X } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"

import {
  EMPLOYER_PLAN_FEATURE_ROWS,
  EMPLOYER_PLAN_TIERS,
  ENTERPRISE_CONTACT_HREF,
  emphasizedEmployerTier,
  employerTierForPlanId,
  type EmployerPlanTierId,
  type TierCell,
} from "./employer-plan-tiers"

type EmployerPlanComparisonProps = {
  currentPlanId: string | null
  canUpgrade: boolean
  upgrading: boolean
  onUpgrade: () => void
}

function TierValue({ value }: { value: TierCell }) {
  if (value.kind === "included") {
    return (
      <span className="inline-flex items-center justify-center gap-2">
        <Check className="size-4 text-success" aria-hidden />
        {value.label ?? "Included"}
      </span>
    )
  }
  if (value.kind === "excluded") {
    return (
      <span className="inline-flex items-center justify-center gap-2 text-muted-foreground">
        <X className="size-4" aria-hidden />
        {value.label ?? "Not included"}
      </span>
    )
  }
  return <span>{value.label}</span>
}

function TierAction({
  tierId,
  isCurrent,
  canUpgrade,
  upgrading,
  onUpgrade,
}: {
  tierId: EmployerPlanTierId
  isCurrent: boolean
  canUpgrade: boolean
  upgrading: boolean
  onUpgrade: () => void
}) {
  if (isCurrent) {
    return (
      <Button variant="successMuted" size="sm" fullWidth disabled>
        Current plan
      </Button>
    )
  }
  if (tierId === "pro" && canUpgrade) {
    return (
      <Button size="sm" fullWidth onClick={onUpgrade} disabled={upgrading}>
        {upgrading ? <Loader2 className="animate-spin" aria-hidden /> : null}
        Upgrade
      </Button>
    )
  }
  if (tierId === "enterprise") {
    return (
      <Button variant="outline" size="sm" fullWidth asChild>
        <a href={ENTERPRISE_CONTACT_HREF}>Contact us</a>
      </Button>
    )
  }
  return null
}

export function EmployerPlanComparison({
  currentPlanId,
  canUpgrade,
  upgrading,
  onUpgrade,
}: EmployerPlanComparisonProps) {
  const currentTier = employerTierForPlanId(currentPlanId)
  const emphasized = emphasizedEmployerTier(currentTier)

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        You pay for matches, not job ads.
      </p>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="sticky left-0 z-20 h-auto min-w-40 bg-card">
              <span className="sr-only">Feature</span>
            </TableHead>
            {EMPLOYER_PLAN_TIERS.map((tier) => {
              const isCurrent = tier.id === currentTier
              const highlight = tier.id === emphasized
              return (
                <TableHead
                  key={tier.id}
                  scope="col"
                  className={cn(
                    "h-auto min-w-44 px-4 align-top text-center",
                    highlight && "bg-primary/5",
                  )}
                >
                  <div className="flex flex-col items-center gap-2 py-2">
                    <span className="text-base font-semibold text-foreground">
                      {tier.name}
                    </span>
                    <Badge
                      variant="outline"
                      className={isCurrent ? undefined : "invisible"}
                      aria-hidden={isCurrent ? undefined : true}
                    >
                      Current
                    </Badge>
                    <span className="text-lg font-semibold tabular-nums text-foreground">
                      {tier.price}
                    </span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {tier.cadence}
                    </span>
                    <div className="w-full pt-2">
                      <TierAction
                        tierId={tier.id}
                        isCurrent={isCurrent}
                        canUpgrade={canUpgrade}
                        upgrading={upgrading}
                        onUpgrade={onUpgrade}
                      />
                    </div>
                  </div>
                </TableHead>
              )
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {EMPLOYER_PLAN_FEATURE_ROWS.map((row) => (
            <TableRow key={row.feature} className="hover:bg-transparent">
              <TableCell
                scope="row"
                className="sticky left-0 z-10 bg-card text-left font-medium"
              >
                {row.feature}
              </TableCell>
              {EMPLOYER_PLAN_TIERS.map((tier) => (
                <TableCell
                  key={tier.id}
                  className={cn(
                    "text-center",
                    tier.id === emphasized && "bg-primary/5",
                  )}
                >
                  <TierValue value={row.cells[tier.id]} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
